
import { useEffect, useState } from 'react';

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBookDemo: () => void;
}

const steps = [
  {
    number: '01',
    icon: 'ri-phone-line',
    title: 'Book a Free Discovery Call',
    description:
      'We start with a quick 15-minute call to understand your business, your goals, and where you\'re losing time or missing opportunities.',
    color: 'from-primary to-teal-400',
  },
  {
    number: '02',
    icon: 'ri-settings-3-line',
    title: 'We Build Your Custom Setup',
    description:
      'Our team configures your AI voice agent, website, scheduler, and marketing tools—tailored specifically to your business and industry.',
    color: 'from-teal-400 to-emerald-500',
  },
  {
    number: '03',
    icon: 'ri-rocket-line',
    title: 'Launch & Go Live',
    description:
      'Within days—not months—your new system is live. We handle the technical details so you can focus on what you do best: running your business.',
    color: 'from-emerald-500 to-green-500',
  },
  {
    number: '04',
    icon: 'ri-line-chart-line',
    title: 'Grow & Optimize',
    description:
      'Track every call, booking, and conversion with clear analytics. We continuously optimize your tools so your results keep improving.',
    color: 'from-green-500 to-primary',
  },
];

export function HowItWorksModal({ isOpen, onClose, onBookDemo }: HowItWorksModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 200);
  };

  const handleBookDemo = () => {
    handleClose();
    setTimeout(onBookDemo, 250);
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        className={`relative bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transition-all duration-200 ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl text-gray-600" />
        </button>

        {/* Header */}
        <div className="px-8 pt-10 pb-6 text-center border-b border-gray-100">
          <span className="text-primary text-xs font-bold tracking-widest uppercase bg-primary/10 px-4 py-2 rounded-full">
            HOW IT WORKS
          </span>
          <h3 className="font-display text-3xl lg:text-4xl font-bold text-secondary mt-5 mb-3">
            From sign-up to success in 4 simple steps
          </h3>
          <p className="text-gray-500 max-w-lg mx-auto text-sm leading-relaxed">
            No tech skills needed. We handle everything so you can focus on your business.
          </p>
        </div>

        {/* Steps */}
        <div className="px-8 py-8 space-y-0">
          {steps.map((step, index) => (
            <div key={step.number} className="relative flex gap-5">
              {/* Vertical connector line */}
              {index < steps.length - 1 && (
                <div className="absolute left-6 top-14 bottom-0 w-px bg-gradient-to-b from-primary/30 to-transparent" />
              )}

              {/* Icon circle */}
              <div className="flex-shrink-0 relative z-10">
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}
                >
                  <i className={`${step.icon} text-xl text-white`} />
                </div>
              </div>

              {/* Content */}
              <div className="pb-8">
                <div className="flex items-center gap-3 mb-1.5">
                  <span className="text-xs font-bold text-primary/60 tracking-widest">
                    STEP {step.number}
                  </span>
                </div>
                <h4 className="font-display text-lg font-bold text-secondary mb-1.5">
                  {step.title}
                </h4>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="px-8 pb-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={handleBookDemo}
            className="bg-gradient-to-r from-primary to-teal-400 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-primary/30 transition-all cursor-pointer whitespace-nowrap"
          >
            Book Your Free Call
          </button>
          <button
            onClick={handleClose}
            className="border-2 border-gray-200 text-secondary px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}
